import { Routes, Route } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import useAuthStore from '@store/authStore';

// Layout
import MainLayout from '@/layouts/MainLayout';
import DashboardLayout from '@/layouts/DashboardLayout';

// Loading component
import LoadingSpinner from '@components/ui/LoadingSpinner';

// Auth guard
import ProtectedRoute from '@components/auth/ProtectedRoute';
import ScrollToTop from '@components/layout/ScrollToTop';

// Lazy load pages
const HomePage = lazy(() => import('@pages/HomePage'));
const AboutPage = lazy(() => import('@pages/AboutPage'));
const AuditPage = lazy(() => import('@pages/AuditPage'));
const ProductsPage = lazy(() => import('@pages/ProductsPage'));
const ProductDetailPage = lazy(() => import('@pages/ProductDetailPage'));
const SealPage = lazy(() => import('@pages/SealPage'));
const VendorDetailPage = lazy(() => import('@pages/VendorDetailPage'));
const QAPage = lazy(() => import('@pages/QAPage'));
const ResourcesPage = lazy(() => import('@pages/ResourcesPage'));
const ContactPage = lazy(() => import('@pages/ContactPage'));
const CartPage = lazy(() => import('@pages/CartPage'));
const CheckoutPage = lazy(() => import('@pages/CheckoutPage'));
const PaymentSuccessPage = lazy(() => import('@pages/PaymentSuccessPage'));
const PaymentCancelPage = lazy(() => import('@pages/PaymentCancelPage'));
const TermsPage = lazy(() => import('@pages/TermsPage'));
const PrivacyPolicyPage = lazy(() => import('@pages/PrivacyPolicyPage'));
const AssessorsDirectoryPage = lazy(() => import('@pages/AssessorsDirectoryPage'));
const AssessorDetailsPage = lazy(() => import('@pages/dashboard/AssessorDetailsPage'));
const AssessorsPage = lazy(() => import('@pages/AssessorsPage'));

// Auth pages
const LoginPage = lazy(() => import('@pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@pages/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@pages/auth/VerifyEmailPage'));
const UserProfilePage = lazy(() => import('@pages/UserProfilePage'));
const AssessorSetupPage = lazy(() => import('@pages/AssessorSetupPage'));

// Dashboard pages
const DashboardHome = lazy(() => import('@pages/dashboard/DashboardHome'));
const ProfilePage = lazy(() => import('@pages/dashboard/ProfilePage'));
const PreAssessmentPage = lazy(() => import('@pages/dashboard/PreAssessmentPage'));
const PreAssessmentQAPage = lazy(() => import('@pages/dashboard/PreAssessmentQAPage'));
const MyAuditsPage = lazy(() => import('@pages/dashboard/MyAuditsPage'));
const WorkshopsPage = lazy(() => import('@pages/dashboard/WorkshopsPage'));
const SubscriptionPage = lazy(() => import('@pages/dashboard/SubscriptionPage'));
const UsersListPage = lazy(() => import('@pages/dashboard/UsersListPage'));
const AuditDetailPage = lazy(() => import('@pages/dashboard/AuditDetailPage'));
const AddressesPage = lazy(() => import('@pages/dashboard/AddressesPage'));
const PaymentMethodsPage = lazy(() => import('@pages/dashboard/PaymentMethodsPage'));
const DownloadsPage = lazy(() => import('@pages/dashboard/DownloadsPage'));
const CommitmentPage = lazy(() => import('@pages/dashboard/CommitmentPage'));
const PreAssessmentReviewPage = lazy(() => import('@pages/dashboard/PreAssessmentReviewPage'));
const AssessorDashboard = lazy(() => import('@pages/dashboard/AssessorDashboard'));
const AssessorAuditPage = lazy(() => import('@pages/dashboard/AssessorAuditPage'));
const SavedVendorsPage = lazy(() => import('@pages/dashboard/SavedVendorsPage'));
const VendorPortfolioPage = lazy(() => import('@pages/dashboard/VendorPortfolioPage'));
const MessagesPage = lazy(() => import('@pages/dashboard/MessagesPage'));
const AdminWorkshopsPage = lazy(() => import('@pages/dashboard/AdminWorkshopsPage'));
const AdminProductsPage = lazy(() => import('@pages/dashboard/AdminProductsPage'));
const AdminBenchmarkPage = lazy(() => import('@pages/dashboard/AdminBenchmarkPage'));
const AssessorApplicationReviewPage = lazy(() => import('@pages/dashboard/AssessorApplicationReviewPage'));


// Error pages
const NotFoundPage = lazy(() => import('@pages/NotFoundPage'));

function App() {
    const { getMe, token } = useAuthStore();

    useEffect(() => {
        if (token) {
            getMe();
        }
    }, []);

    return (
        <Suspense fallback={<LoadingSpinner fullScreen />}>
            <ScrollToTop />
            <Routes>
                {/* Public routes with main layout */}
                <Route element={<MainLayout />}>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/audit" element={<AuditPage />} />
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/products/:id" element={<ProductDetailPage />} />
                    <Route path="/the-seal-voe-directory" element={<SealPage />} />
                    <Route path="/directory/:id" element={<VendorDetailPage />} />
                    <Route path="/qa" element={<QAPage />} />
                    <Route path="/assessors" element={<AssessorsPage />} />
                    <Route path="/resources" element={<ResourcesPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/payment/success" element={<PaymentSuccessPage />} />
                    <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPolicyPage />} />
                    <Route path="/profile" element={
                        <ProtectedRoute>
                            <UserProfilePage />
                        </ProtectedRoute>
                    } />
                </Route>

                {/* Auth routes (no layout) */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
                <Route path="/assessor-setup" element={<AssessorSetupPage />} />

                {/* Protected dashboard routes */}
                <Route element={<ProtectedRoute roles={['admin', 'candidate', 'certified_vendor', 'free_user', 'studio', 'assessor']} />}>
                    <Route element={<DashboardLayout />}>
                        <Route path="/dashboard" element={<DashboardHome />} />
                        <Route path="/dashboard/profile" element={<ProfilePage />} />
                        <Route path="/dashboard/messages" element={<MessagesPage />} />
                        <Route path="/dashboard/saved-vendors" element={<SavedVendorsPage />} />
                        <Route path="/dashboard/portfolio" element={<VendorPortfolioPage />} />
                        <Route path="/dashboard/addresses" element={
                            <ProtectedRoute roles={['candidate', 'certified_vendor', 'free_user', 'studio', 'assessor']}>
                                <AddressesPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/dashboard/payment-methods" element={<PaymentMethodsPage />} />
                        <Route path="/dashboard/downloads" element={<DownloadsPage />} />
                        <Route path="/dashboard/pre-assessment" element={<PreAssessmentPage />} />
                        <Route path="/dashboard/pre-assessment/help" element={<PreAssessmentQAPage />} />
                        <Route path="/dashboard/audits" element={<MyAuditsPage />} />
                        <Route path="/dashboard/workshops" element={<WorkshopsPage />} />
                        <Route path="/dashboard/subscription" element={<SubscriptionPage />} />
                        <Route path="/dashboard/admin/users" element={<UsersListPage />} />
                        <Route path="/dashboard/audits/:id" element={<AuditDetailPage />} />
                        <Route path="/dashboard/commitment/:auditId" element={<CommitmentPage />} />
                        <Route path="/dashboard/admin/pre-assessments" element={<PreAssessmentReviewPage />} />
                        <Route path="/dashboard/assessor" element={<AssessorDashboard />} />
                        <Route path="/dashboard/assessor/audit/:id" element={<AssessorAuditPage />} />
                        <Route path="/dashboard/admin/workshops" element={
                            <ProtectedRoute roles={['admin']}>
                                <AdminWorkshopsPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/dashboard/admin/products" element={
                            <ProtectedRoute roles={['admin']}>
                                <AdminProductsPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/dashboard/admin/benchmarks" element={
                            <ProtectedRoute roles={['admin']}>
                                <AdminBenchmarkPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/dashboard/admin/assessor-recruitments" element={
                            <ProtectedRoute roles={['admin']}>
                                <AssessorApplicationReviewPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/dashboard/assessors-directory" element={
                            <ProtectedRoute roles={['admin', 'candidate', 'certified_vendor', 'studio']}>
                                <AssessorsDirectoryPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/dashboard/assessors-directory/:id" element={
                            <ProtectedRoute roles={['admin']}>
                                <AssessorDetailsPage />
                            </ProtectedRoute>
                        } />
                    </Route>
                </Route>



                {/* 404 */}
                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </Suspense>
    );
}

export default App;
